import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";

/**
 * StandardCTA — uniform bottom-of-page CTA across the entire site.
 *
 * Locked layout (mirrors the Book Demo "Transform The Way" CTA):
 *   • Full-bleed background image covers the ENTIRE card (no negative space)
 *   • Copy sits on the left over a soft horizontal vignette so it stays readable
 *   • Buttons stack vertically full-width on the right
 *   • Top hairline, top feather, and side vignette layered above the image
 *
 * Props:
 *   eyebrow         — optional small uppercase label (default "GET STARTED")
 *   eyebrowTone     — "positioning" (amber) | "intelligence" (sapphire)  [default positioning]
 *   showEyebrow     — set to false to omit the eyebrow entirely (default true)
 *   title           — string (can include the highlight substring)
 *   highlight       — substring of `title` rendered in sapphire glow
 *   subtitle        — supporting paragraph
 *   crystal         — image URL used as the full-bleed background
 *   primaryLabel    — primary button text (default "Book a Demo")
 *   primaryHref     — primary route (default "/book-demo")
 *   secondaryLabel  — secondary button text (default "Get in Touch")
 *   secondaryHref   — secondary route (default "/contact")
 *   testId          — data-testid prefix
 *   imageFilter     — override the default filter recipe if the asset needs a bespoke tone
 */
export default function StandardCTA({
  eyebrow = "GET STARTED",
  eyebrowTone = "positioning",
  showEyebrow = true,
  title,
  highlight,
  subtitle,
  crystal,
  primaryLabel = "Book a Demo",
  primaryHref = "/book-demo",
  secondaryLabel = "Get in Touch",
  secondaryHref = "/contact",
  testId = "standard-cta",
  imageFilter = "brightness(0.78) contrast(1.18) saturate(1.20)",
}) {
  const navigate = useNavigate();

  // Split title on highlight for the sapphire-glow span.
  const renderTitle = () => {
    if (!highlight || !title?.includes(highlight)) return title;
    const [before, after] = title.split(highlight);
    return (
      <>
        {before}
        <span
          className="text-sapphire-glow"
          style={{ textShadow: "0 0 30px rgba(96,165,250,0.45)" }}
        >
          {highlight}
        </span>
        {after}
      </>
    );
  };

  return (
    <section
      className="relative pb-20 lg:pb-24 overflow-hidden"
      data-testid={`${testId}-section`}
    >
      <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
        <div
          className="relative rounded-2xl overflow-hidden border border-white/[0.08] min-h-[300px] lg:min-h-[360px] fade-up"
          style={{
            background:
              "linear-gradient(135deg, rgba(13,30,62,0.55) 0%, rgba(10,15,26,0.92) 60%, rgba(10,15,26,0.95) 100%)",
          }}
          data-testid={testId}
        >
          {/* Top hairline */}
          <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-amber-300/55 to-transparent" />

          {/* FULL-BLEED background asset — covers the ENTIRE card */}
          {crystal && (
            <div className="absolute inset-0 pointer-events-none" aria-hidden>
              <img
                src={crystal}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover object-center"
                style={{
                  filter: imageFilter,
                  WebkitMaskImage:
                    "linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.85) 40%, rgba(0,0,0,0.95) 100%)",
                  maskImage:
                    "linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.85) 40%, rgba(0,0,0,0.95) 100%)",
                }}
                draggable="false"
              />
              {/* Horizontal vignette keeps the left-side copy readable */}
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(90deg, rgba(10,15,26,0.88) 0%, rgba(10,15,26,0.55) 38%, rgba(10,15,26,0.18) 70%, rgba(10,15,26,0.05) 100%)",
                }}
              />
              {/* Top edge feather into the section above */}
              <div
                className="absolute top-0 inset-x-0 h-24 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(10,15,26,0.85), transparent)",
                }}
              />
            </div>
          )}

          {/* CONTENT — text left, buttons stacked right */}
          <div className="relative z-10 grid lg:grid-cols-[1.4fr_1fr] gap-10 items-center p-10 sm:p-14 lg:p-16">
            <div className="fade-up">
              {showEyebrow && (
                <div
                  className={
                    eyebrowTone === "intelligence"
                      ? "eyebrow-intelligence mb-5"
                      : "eyebrow-positioning mb-5"
                  }
                >
                  {eyebrow}
                </div>
              )}
              <h3 className="font-display text-platinum text-[28px] sm:text-[34px] lg:text-[40px] leading-[1.08] tracking-[-0.018em]">
                {renderTitle()}
              </h3>
              {subtitle && (
                <p className="mt-5 text-[14px] sm:text-[15px] text-platinum/72 leading-[1.7] max-w-[520px]">
                  {subtitle}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-3 fade-up" style={{ animationDelay: "120ms" }}>
              <button
                onClick={() => navigate(primaryHref)}
                data-testid={`${testId}-primary`}
                className="btn-primary rounded-md h-12 px-6 text-[14px] font-medium inline-flex items-center justify-between gap-2 w-full"
              >
                {primaryLabel} <ArrowRight className="w-4 h-4" />
              </button>
              {secondaryLabel && (
                <button
                  onClick={() => navigate(secondaryHref)}
                  data-testid={`${testId}-secondary`}
                  className="btn-ghost rounded-md h-12 px-6 text-[14px] font-medium inline-flex items-center justify-between gap-2 w-full"
                >
                  {secondaryLabel} <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
